import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, AlertTriangle, PauseCircle } from 'lucide-react';
import { campaignsAPI } from '../services/api.js';
import { formatDateTimeWithZone, formatPhone } from '../lib/format.js';

// UPCOMING CALLS — the answer to "what is Alfred going to do next?"
//
// WHY IT EXISTS. Brian scheduled three follow-ups and had no way to see them:
// callbacks live in their own table with their own `matter`, they are NOT
// `campaign_contacts.scheduledAt`, and until Charlie shipped GET /callbacks they
// were only listable one contact at a time. A promise to ring a business back that
// you cannot see anywhere is a promise you will not keep.
//
// ⛔ THE ONE THING THIS MUST NOT DO IS SHOW A DUE CALL THAT CANNOT FIRE.
// The dialer only walks campaigns with status 'active' (dialerWorker tick), and
// callbacks are processed inside that loop — so a pending callback on a PAUSED
// campaign is never even examined. It does not error, does not retry; it just sits.
// Brian's three sat behind a paused campaign for a day. So `campaignStatus` is
// rendered as loudly as the time is: a call that will not happen has to say so.
export default function UpcomingCalls({ limit = 8, campaignId = null, title = 'Upcoming calls' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    campaignsAPI.callbackQueue({ status: 'pending', limit: 100 })
      .then((r) => {
        if (cancelled) return;
        const all = r?.data || [];
        setRows(campaignId ? all.filter((x) => x.campaignId === campaignId) : all);
        setUnavailable(false);
      })
      .catch((err) => {
        // Authenticated request → a missing route is a real 404 here.
        if (!cancelled) setUnavailable(err.status === 404);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [campaignId]);

  // Nothing scheduled is a legitimate, quiet state — don't take up room saying so
  // on a page that has other work on it.
  if (loading || unavailable || rows.length === 0) return null;

  const shown = rows.slice(0, limit);
  const blocked = rows.filter((r) => r.campaignStatus && r.campaignStatus !== 'active');
  const overdue = rows.filter((r) => r.overdue);

  return (
    <section className="card mb-6">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">{title}</h2>
          <span className="badge bg-stone-800 text-stone-400 border border-stone-700 text-[10px]">
            {rows.length} pending
          </span>
        </div>
      </header>

      {/* The warning that would have saved a day. */}
      {blocked.length > 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          <PauseCircle size={14} className="mt-0.5 shrink-0" />
          <span>
            {blocked.length === rows.length ? 'These' : `${blocked.length} of these`} won&rsquo;t be
            called — {blocked.length === 1 ? 'its campaign is' : 'their campaigns are'} paused, and
            the dialer only runs active campaigns. Start the campaign and they go out at their time.
          </span>
        </p>
      )}
      {overdue.length > 0 && blocked.length === 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {overdue.length} {overdue.length === 1 ? 'is' : 'are'} past due and should already have gone out.
        </p>
      )}

      <ul className="divide-y divide-stone-800">
        {shown.map((r) => {
          const willNotFire = r.campaignStatus && r.campaignStatus !== 'active';
          return (
            <li key={r.id} className="py-2.5 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <div className="min-w-0">
                  <span className="text-stone-200">{r.company || r.name || formatPhone(r.phoneE164)}</span>
                  {r.phoneE164 && <span className="text-stone-500"> · {formatPhone(r.phoneE164)}</span>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={r.overdue ? 'text-amber-300' : 'text-stone-400'}>
                    {formatDateTimeWithZone(r.scheduledAt)}
                  </span>
                  {willNotFire && (
                    <span className="badge bg-amber-600/20 text-amber-200 border border-amber-600/40 text-[10px]">
                      {r.campaignStatus}
                    </span>
                  )}
                </div>
              </div>
              {/* The matter IS the call's goal — it becomes the prompt. Showing the
                  time without it tells you a call is coming but not what it is. */}
              {r.matter && (
                <p className="mt-1 line-clamp-2 text-xs text-stone-500">{r.matter}</p>
              )}
              {!campaignId && r.campaignName && (
                <Link
                  to={`/campaigns/${r.campaignId}`}
                  className="mt-0.5 inline-block text-xs text-stone-500 underline-offset-2 hover:text-alfred-gold hover:underline"
                >
                  {r.campaignName}
                </Link>
              )}
            </li>
          );
        })}
      </ul>

      {rows.length > shown.length && (
        <p className="mt-2 text-xs text-stone-500">…and {rows.length - shown.length} more.</p>
      )}
    </section>
  );
}
